import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@common/bw-ui/lib/utils';
import { CheckIcon, ChevronDownIcon } from '@common/bw-ui/icons';
import { useFormControl } from './form-field';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, asChild, ...props }, ref) => {
  const controlProps = useFormControl(props);
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      asChild={asChild}
      data-slot="select-trigger"
      className={
        asChild
          ? undefined
          : cn(
              // data-success / aria-invalid mirror the Input validated states: success
              // is icon-only (status icon passed in as a child after SelectValue),
              // invalid also restyles the field red
              // Focus is a soft blurred glow that matches the trigger's border colour
              // (grey normally, red while invalid) rather than a hard focus ring.
              // theme() not var(): consumers import the theme with prefix(tw), which
              // renames the emitted variables, so unprefixed var() refs silently
              // resolve to nothing at runtime.
              'tw:flex tw:h-12 tw:w-full tw:cursor-pointer tw:items-center tw:justify-between tw:gap-2 tw:rounded-lg tw:border tw:border-border-strong tw:bg-background tw:px-2.5 tw:py-2 tw:text-base tw:font-normal tw:text-foreground tw:focus-visible:outline-none tw:focus-visible:shadow-[0_0_2px_1px_theme(--color-border-strong)] tw:aria-[invalid=true]:focus-visible:shadow-[0_0_2px_1px_theme(--color-destructive)] tw:disabled:cursor-not-allowed tw:disabled:opacity-50 tw:data-[placeholder]:text-muted tw:aria-[invalid=true]:border-2 tw:aria-[invalid=true]:border-destructive tw:aria-[invalid=true]:bg-destructive-light [&>span]:tw:min-w-0 [&>span]:tw:truncate',
              className,
            )
      }
      // Safari's default Tab order skips <button> elements unless they carry
      // an explicit tabindex — without this a keyboard user can't reach the
      // select at all there.
      tabIndex={0}
      {...props}
      {...controlProps}
    >
      {asChild ? (
        children
      ) : (
        <>
          {children}
          <SelectPrimitive.Icon asChild>
            <ChevronDownIcon
              className="tw:size-4 tw:shrink-0 tw:text-foreground"
              aria-hidden="true"
            />
          </SelectPrimitive.Icon>
        </>
      )}
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = 'SelectTrigger';

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      data-slot="select-content"
      className={cn(
        'tw:relative tw:z-50 tw:max-h-[min(24rem,var(--radix-select-content-available-height))] tw:min-w-[8rem] tw:overflow-hidden tw:rounded-lg tw:border tw:border-border tw:bg-background tw:text-foreground tw:shadow-md',
        position === 'popper' &&
          'tw:w-full tw:min-w-[var(--radix-select-trigger-width)] tw:data-[side=bottom]:translate-y-1 tw:data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport className="tw:p-1">
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = 'SelectContent';

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'tw:px-2.5 tw:py-1.5 tw:text-sm tw:font-medium tw:text-muted',
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = 'SelectLabel';

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    data-slot="select-item"
    className={cn(
      'tw:relative tw:flex tw:w-full tw:cursor-pointer tw:select-none tw:items-center tw:rounded-md tw:py-2 tw:pl-2.5 tw:pr-8 tw:text-base tw:font-normal tw:text-foreground tw:outline-none tw:focus:bg-secondary tw:data-[disabled]:pointer-events-none tw:data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className="tw:absolute tw:right-2.5 tw:flex tw:items-center tw:justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="tw:size-3.5 tw:text-primary" aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = 'SelectItem';

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('tw:-mx-1 tw:my-1 tw:h-px tw:bg-border', className)}
    {...props}
  />
));
SelectSeparator.displayName = 'SelectSeparator';

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};
